import React from "react";
import { Document, Page, Text } from "@react-pdf/renderer";
import {
  Results,
  Slide1Type,
  Slide2Type,
  Slide3Type,
  Slide4Type,
  Slide5Part1Type,
  Slide6Type,
  Slide8Type,
  SlideName,
} from "./constants/Questions";
import {
  CallOptions,
  CliffPeriod,
  Company,
  CountryType,
  FoundersType,
  ResultType as ResultsType,
  ShareholdersType,
  TimeType,
  vestedShares,
  VestingFrequency,
} from "./ResultContext";
import { Slide11 } from "./results/Slide11";
import { Slide4 } from "./results/Slide4";
import { Slide6 } from "./results/Slide6";
import { Slide3 } from "./results/Slide3";
import { Slide2 } from "./results/Slide2";
import { Slide5Part1 } from "./results/Slide5Part1";
import { Slide5Part2 } from "./results/Slide5Part2";
import { Slide8 } from "./results/Slide8";
import { Slide1 } from "./results/Slide1";
import { Slide10 } from "./results/Slide10";

export function Pdf({
  result,
  image,
}: {
  result: Record<number, ResultsType>;
  image: string;
}) {
  const sortedResults = Results.sort((a, b) => a.position - b.position);

  return (
    <Document>
      {sortedResults.map((item) => {
        if (item.resultType === SlideName.Slide1) {
          const typedItem = item as Slide1Type;
          const foundersResult = result[typedItem.foundersQuestionId] as {
            name: string;
            country: CountryType;
            type: FoundersType;
          }[];
          const companyResult = result[typedItem.companyQuestionId] as Company;

          return (
            <Page orientation={"landscape"}>
              <Slide1
                foundersResult={foundersResult}
                companyResult={companyResult}
              />
            </Page>
          );
        }
        if (item.resultType === SlideName.Slide2) {
          const typedItem = item as Slide2Type;
          const foundersResult = result[typedItem.foundersQuestionId] as {
            name: string;
            country: CountryType;
            type: FoundersType;
          }[];
          const companyResult = result[typedItem.companyQuestionId] as Company;
          const sharesResult = result[
            typedItem.sharesQuestionId
          ] as ShareholdersType;

          return (
            <Page orientation={"landscape"}>
              <Slide2
                image={image}
                foundersResult={foundersResult}
                companyResult={companyResult}
                sharesResult={sharesResult}
              />
            </Page>
          );
        }

        if (item.resultType === SlideName.Slide3) {
          const typedItem = item as Slide3Type;
          const foundersResult = result[typedItem.foundersQuestionId] as {
            name: string;
            country: CountryType;
            type: FoundersType;
          }[];

          const vestingDurationResult = result[typedItem.vestingDurationId] as {
            time: number;
            type: TimeType;
          };

          const timeCommitmentResult = result[typedItem.timeCommitmentId] as {
            timeHours: string;
            timeWeeks: string;
          }[];

          return (
            <Page orientation={"landscape"}>
              <Slide3
                foundersResult={foundersResult}
                vestingDurationResult={vestingDurationResult}
                timeCommitmentResult={timeCommitmentResult}
              />
            </Page>
          );
        }

        if (item.resultType === SlideName.Slide4) {
          const typedItem = item as Slide4Type;
          const foundersResult = result[typedItem.foundersQuestionId] as {
            name: string;
            country: CountryType;
            type: FoundersType;
          }[];
          const rolesResponsibilitiesResult = result[
            typedItem.rolesResponsibilitiesId
          ] as { tags: string[]; extra: string }[];
          return (
            <Page orientation={"landscape"}>
              <Slide4
                foundersResult={foundersResult}
                rolesResponsibilitiesResult={rolesResponsibilitiesResult}
              />
            </Page>
          );
        }

        if (item.resultType === SlideName.Slide5Part1) {
          const typedItem = item as Slide5Part1Type;
          const goodLeaverEventsResult = result[
            typedItem.leaverEventGoodId
          ] as {
            tags: string[];
            extra: string;
          };
          const earlyLeaverEventsResult = result[
            typedItem.leaverEventEarlyId
          ] as {
            tags: string[];
            extra: string;
          };
          const badLeaverEventsResult = result[typedItem.leaverEventBadId] as {
            tags: string[];
            extra: string;
          };

          return (
            <Page orientation={"landscape"}>
              <Slide5Part1
                goodLeaverEventsResult={goodLeaverEventsResult}
                earlyLeaverEventsResult={earlyLeaverEventsResult}
                badLeaverEventsResult={badLeaverEventsResult}
              />
            </Page>
          );
        }

        if (item.resultType === SlideName.Slide5Part2) {
          const typedItem = item as Slide5Part1Type;
          const goodLeaverEventsResult = result[
            typedItem.leaverEventGoodId
          ] as {
            tags: string[];
            extra: string;
          };
          const earlyLeaverEventsResult = result[
            typedItem.leaverEventEarlyId
          ] as {
            tags: string[];
            extra: string;
          };
          const badLeaverEventsResult = result[typedItem.leaverEventBadId] as {
            tags: string[];
            extra: string;
          };
          if (
            !goodLeaverEventsResult ||
            !earlyLeaverEventsResult ||
            !badLeaverEventsResult
          ) {
            return (
              <Page orientation={"landscape"}>
                <Text>Not all information is filled in for this section</Text>
              </Page>
            );
          }
          if (
            goodLeaverEventsResult.tags.includes("Other") ||
            earlyLeaverEventsResult.tags.includes("Other") ||
            badLeaverEventsResult.tags.includes("Other")
          ) {
            return (
              <Page orientation={"landscape"}>
                <Slide5Part2
                  goodLeaverEventsResult={goodLeaverEventsResult}
                  earlyLeaverEventsResult={earlyLeaverEventsResult}
                  badLeaverEventsResult={badLeaverEventsResult}
                />
              </Page>
            );
          }
        }

        if (item.resultType === SlideName.Slide6) {
          const typedItem = item as Slide6Type;
          const vestingFrequencyResult = result[
            typedItem.vestingFrequencyId
          ] as {
            frequency: VestingFrequency;
            type: string;
          };
          const vestingDurationResult = result[typedItem.vestingDurationId] as {
            time: number;
            type: TimeType;
          };
          const vestingCliffResult = result[
            typedItem.vestingCliffId
          ] as CliffPeriod;

          const vestingStartResult = result[
            typedItem.vestingStartId
          ] as string[];
          const foundersResult = result[typedItem.foundersQuestionId] as {
            name: string;
            country: CountryType;
            type: FoundersType;
          }[];
          const callOptionsGoodResult = result[typedItem.callOptionsGoodId] as {
            vested: CallOptions;
            unvested: CallOptions;
            vestedBad: CallOptions;
          };
          const callOptionsEarlyResult = result[
            typedItem.callOptionsEarlyId
          ] as {
            vested: CallOptions;
            unvested: CallOptions;
            vestedBad: CallOptions;
          };
          const callOptionsBadResult = result[typedItem.callOptionsBadId] as {
            vested: CallOptions;
            unvested: CallOptions;
            vestedBad: CallOptions;
          };
          const badAfterVestingResult = result[
            typedItem.badAfterVestingId
          ] as boolean;
          const vestingSharesResult = result[
            typedItem.vestingSharesId
          ] as vestedShares;
          return (
            <Page orientation={"landscape"}>
              <Slide6
                vestingFrequencyResult={vestingFrequencyResult}
                vestingDurationResult={vestingDurationResult}
                vestingCliffResult={vestingCliffResult}
                vestingStartResult={vestingStartResult}
                foundersResult={foundersResult}
                callOptionsGoodResult={callOptionsGoodResult}
                callOptionsEarlyResult={callOptionsEarlyResult}
                callOptionsBadResult={callOptionsBadResult}
                badAfterVestingResult={badAfterVestingResult}
                vestingSharesResult={
                  vestingSharesResult
                    ? vestingSharesResult.map((s) => s.vestedOptions)
                    : []
                }
              />
            </Page>
          );
        }

        if (item.resultType === SlideName.Slide8) {
          const typedItem = item as Slide8Type;
          const vestingDurationResult = result[typedItem.vestingDurationId] as {
            time: number;
            type: TimeType;
          };
          const vestingCliffResult = result[
            typedItem.vestingCliffId
          ] as CliffPeriod;
          const vestingFrequencyResult = result[
            typedItem.vestingFrequencyId
          ] as {
            frequency: VestingFrequency;
            type: string;
          };
          const callOptionsGoodResult = result[typedItem.callOptionsGoodId] as {
            vested: CallOptions;
            unvested: CallOptions;
            vestedBad: CallOptions;
          };
          const callOptionsEarlyResult = result[
            typedItem.callOptionsEarlyId
          ] as {
            vested: CallOptions;
            unvested: CallOptions;
            vestedBad: CallOptions;
          };
          const callOptionsBadResult = result[typedItem.callOptionsBadId] as {
            vested: CallOptions;
            unvested: CallOptions;
            vestedBad: CallOptions;
          };
          return (
            <Page orientation={"landscape"}>
              <Slide8
                vestingDurationResult={vestingDurationResult}
                vestingCliffResult={vestingCliffResult}
                vestingFrequencyResult={vestingFrequencyResult}
                callOptionsGoodResult={callOptionsGoodResult}
                callOptionsEarlyResult={callOptionsEarlyResult}
                callOptionsBadResult={callOptionsBadResult}
              />
            </Page>
          );
        }
      })}
      <Page orientation={"landscape"}>
        <Slide10 />
      </Page>
      <Page orientation={"landscape"}>
        <Slide11 />
      </Page>
    </Document>
  );
}
